import { useCallback } from 'react';
import { useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useIsCustomerAddress } from '../../hooks';
import { useNavigateToMainTabs } from '../../navigation';
import { useLastInteractedStore } from './useLastInteractedStore';

/**
 * Navigates to the last interacted store if available.
 * Fallback is to navigate to the menu tab itself.
 */
export const useNavigateToLastInteractedStore = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const router = useSafeRouter();
  const navigateToMainTabs = useNavigateToMainTabs();

  const lastInteractedStore = useLastInteractedStore();
  const { restaurantSlug = '', addressId } = lastInteractedStore ?? {};

  const isCustomerAddress = useIsCustomerAddress(addressId);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const navigateToLastInteractedStore = useCallback(() => {
    if (restaurantSlug) {
      void navigateToMainTabs('MenuTab', {
        screen: 'Menu',
        params: { restaurantSlug },
      });

      return;
    }

    if (addressId && isCustomerAddress) {
      void navigateToMainTabs('MenuTab', {
        screen: 'DeliveryMenu',
        params: { addressId },
      });

      return;
    }

    void navigateToMainTabs('MenuTab');
  }, [navigateToMainTabs, restaurantSlug, addressId, isCustomerAddress]);

  const navigateToLastInteractedStoreExpoRouter = useCallback(() => {
    if (restaurantSlug) {
      router?.navigate({ pathname: '/menu/[restaurantSlug]/menu', params: { restaurantSlug } }); // prettier-ignore
      return;
    }

    if (addressId && isCustomerAddress) {
      router?.navigate({ pathname: '/menu/delivery/[addressId]/menu', params: { addressId } }); // prettier-ignore
      return;
    }

    router?.navigate('/menu/locations');
  }, [router, restaurantSlug, addressId, isCustomerAddress]);

  // ─────────────────────────────────────────────────────────────────────────────

  return isExpoRouterEnabled
    ? navigateToLastInteractedStoreExpoRouter
    : navigateToLastInteractedStore;
};
